import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import type { SortOption, Timeframe } from '../../../data/events/usePaginatedEventsQuery.ts';
import type { EventTypeFilter } from '../components/SortFilterMenu.tsx';

const SORT_OPTIONS: ReadonlySet<string> = new Set<SortOption>([
  'start',
  '-start',
  'name',
  '-name',
  'date_created',
  '-date_created',
]);
const isSortOption = (value: string): value is SortOption => SORT_OPTIONS.has(value);
const EVENT_TYPES: ReadonlySet<string> = new Set<NonNullable<EventTypeFilter>>(['online', 'live', 'secret']);
const isEventType = (value: string): value is NonNullable<EventTypeFilter> => EVENT_TYPES.has(value);
const TIMEFRAMES: ReadonlySet<string> = new Set<Timeframe>(['upcoming', 'all']);
const isTimeframe = (value: string): value is Timeframe => TIMEFRAMES.has(value);

const DEFAULT_SORT: SortOption = 'start';
const DEFAULT_TIMEFRAME: Timeframe = 'upcoming';

interface DashboardParams {
  sort: SortOption;
  categoryId: number | undefined;
  eventType: EventTypeFilter;
  timeframe: Timeframe;
  page: number;
}

type Patch = Partial<DashboardParams>;

/**
 * Keeps the dashboard's filters, sorting, timeframe and page in the query string, so a view can
 * be linked, bookmarked and survives a reload.
 *
 * Values are validated rather than trusted: a hand-edited or stale URL falls back to the default
 * instead of sending nonsense to Directus. Defaults are left out of the URL to keep it readable.
 *
 * History entries are replaced rather than pushed. Changing a filter is not a navigation, and
 * pushing would bury the previous page under a dozen filter states.
 */
export const useDashboardParams = (): DashboardParams & {
  setParams: (patch: Patch) => void;
  clearFilters: () => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<DashboardParams>(() => {
    const sort = searchParams.get('sort');
    const type = searchParams.get('type');
    const timeframe = searchParams.get('timeframe');
    const category = Number.parseInt(searchParams.get('category') ?? '', 10);
    const page = Number.parseInt(searchParams.get('page') ?? '', 10);

    return {
      categoryId: Number.isFinite(category) && category > 0 ? category : undefined,
      eventType: type && isEventType(type) ? type : undefined,
      page: Number.isFinite(page) && page > 0 ? page : 1,
      sort: sort && isSortOption(sort) ? sort : DEFAULT_SORT,
      timeframe: timeframe && isTimeframe(timeframe) ? timeframe : DEFAULT_TIMEFRAME,
    };
  }, [searchParams]);

  const setParams = useCallback(
    (patch: Patch) => {
      const next = { ...params, ...patch };

      // Anything that changes which events match sends you back to the first page, otherwise
      // you can land on page 4 of a two-page result.
      if (patch.page === undefined) next.page = 1;

      const query = new URLSearchParams();
      if (next.timeframe !== DEFAULT_TIMEFRAME) query.set('timeframe', next.timeframe);
      if (next.sort !== DEFAULT_SORT) query.set('sort', next.sort);
      if (next.categoryId) query.set('category', String(next.categoryId));
      if (next.eventType) query.set('type', next.eventType);
      if (next.page > 1) query.set('page', String(next.page));

      setSearchParams(query, { replace: true });
    },
    [params, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setParams({ categoryId: undefined, eventType: undefined, sort: DEFAULT_SORT });
  }, [setParams]);

  return { ...params, clearFilters, setParams };
};
